import React, { useRef, useEffect, useState } from 'react';
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import PropTypes from 'prop-types';
import ChartTooltip from './ChartTooltip';
import {colors} from '../../utils/colors.js';
import {getCSSVariable} from "../../utils/getCSSVariable.js";
import {formatCurrency} from "../../utils/formatCurrency.js";
import FlexContainer from "../FlexContainer/FlexContainer.jsx";

Chart.register(BarController, BarElement, CategoryScale, Filler, LinearScale,  LineController, LineElement, Tooltip, Legend);

const isPlainObject = (val) =>
  val !== null && typeof val === 'object' && val.constructor === Object;

const deepMerge = (target = {}, source = {}) => {
  const out = { ...target };
  Object.keys(source).forEach((key) => {
    const srcVal = source[key];
    const tgtVal = target[key];

    if (isPlainObject(srcVal) && isPlainObject(tgtVal)) {
      out[key] = deepMerge(tgtVal, srcVal);
    } else {
      out[key] = srcVal;
    }
  });
  return out;
};

const BarsAndLineChart = ({
  labels,
  datasets,
  width = '100%',
  height = 300,
  options = {},
  stacked = false,
  maintainAspectRatio = false,
  roundedBars = true,
  barSpacing = 2,
  cleanGrid = true,
  pointRadius = 4,
  pointHoverRadius = 6,
  lineTension = 0,
  currency = null,
  labelsToDisplayCurrency = [],
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    label: '',
    value: '',
    color: '',
    title: '',
    allDataPoints: [],
  });

  // Track mouse position over the canvas
  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas) return;
    const handleMouseMove = (e) => {
      if (!tooltip.visible) return;
      setTooltip((prev) => ({
        ...prev,
        x: e.clientX, // Use viewport coordinates for fixed positioning
        y: e.clientY,
      }));
    };
    canvas.addEventListener('mousemove', handleMouseMove);
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [tooltip.visible]);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    const ctx = chartRef.current.getContext('2d');

    const spacingPlugin = {
      id: 'spacingPlugin',
      afterDatasetsDraw: (chart) => {
        if (!stacked || barSpacing <= 0) return;

        const ctx = chart.ctx;
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          if (dataset.type !== 'bar') return;
          if (datasetIndex === 0) return; // skip bottom

          const meta = chart.getDatasetMeta(datasetIndex);
          const prevMeta = chart.getDatasetMeta(datasetIndex - 1);

          meta.data.forEach((bar, index) => {
            const prevBar = prevMeta.data[index];
            if (prevBar && bar) {
              const gap = barSpacing;
              // shift current bar down (for positive bars)
              if (bar.y < prevBar.y) {
                bar.y += gap;
                bar.height -= gap;
              } else { // shift for negative bars
                bar.y -= gap;
                bar.height -= gap;
              }
            }
          });
        });
      },
    };

    const defaultOptions = {
      maintainAspectRatio,
      responsive: true,
      plugins: {
        spacingPlugin,
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          enabled: false,
          external: (context) => {
            const tooltipModel = context.tooltip;
            if (tooltipModel.opacity === 0) {
              setTooltip((prev) => ({ ...prev, visible: false }));
              return;
            }
            if (tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0) {
              const dataPoints = tooltipModel.dataPoints.map(dp => ({
                label: dp.dataset.label,
                value: dp.raw,
                color: dp.element.options.backgroundColor,
              }));

              const primaryDp = tooltipModel.dataPoints[0];
              const labelIndex = primaryDp.dataIndex;
              const chartLabel = context.chart.data.labels[labelIndex];

              setTooltip((prev) => ({
                ...prev,
                visible: true,
                label: primaryDp.dataset.label || primaryDp.label,
                value: primaryDp.raw,
                color: primaryDp.element.options.backgroundColor,
                title: chartLabel,
                allDataPoints: dataPoints,
              }));
            }
          },
        },
      },
      scales: {
        x: {
          stacked: stacked,
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: colors.bgDark,
            font: { size: 12 },
          },
        },
        y: {
          stacked: stacked,
          grid: cleanGrid
            ? {
              display: true,
              drawBorder: false,
              color: getCSSVariable('--bg-dark'),
              lineWidth: 1,
            }
            : { display: true },
          border: { display: false },
          ticks: {
            color: colors.bgDark,
            font: { size: 12 },
          },
          position: 'left',
          beginAtZero: true,
        },
        y1: {
          display: true,
          position: 'right',
          beginAtZero: true,
          grid: {
            drawOnChartArea: false,
            display: false,
          },
        }
      },
      hover: {
        mode: 'index',
        intersect: false,
      },
      elements: {
        bar: {
          borderSkipped: false,
          borderRadius: (ctx) => {
            const { datasetIndex, dataIndex, chart } = ctx;
            const barDatasets = chart.data.datasets.filter(dataset => dataset.type === 'bar')
            const datasetCount = barDatasets.length;
            const value = ctx.raw;

            if (!stacked) return roundedBars ? 6 : 0;

            const datasets = barDatasets;
            const prevValue =
              datasetIndex > 0 ? datasets[datasetIndex - 1].data[dataIndex] : 0;
            const nextValue =
              datasetIndex < datasetCount - 1
                ? datasets[datasetIndex + 1].data[dataIndex]
                : 0;

            if (value === 0) return 0;

            if (datasetIndex === 0) {
              return {
                topLeft: 0,
                topRight: 0,
                bottomLeft: 6,
                bottomRight: 6,
              };
            } else if (datasetIndex === datasetCount - 1) {
              return {
                topLeft: 6,
                topRight: 6,
                bottomLeft: 0,
                bottomRight: 0,
              };
            } else {
              return 0;
            }
          },
        },
        line: {
          tension: lineTension,
        },
        point: {
          radius: pointRadius,
          hoverRadius: pointHoverRadius,
        },
      },
      datasets: {
        bar: {
          maxBarThickness: 30,
        },
      },
      layout: {
        padding: {
          top: 10,
          bottom: 10,
        },
      },
    };

    const mergedOptions = deepMerge(defaultOptions, options || {});

    chartInstance.current = new Chart(ctx, {
      data: {
        labels: labels,
        datasets: datasets,
      },
      options: mergedOptions,
    });

    return () => {
      chartInstance.current && chartInstance.current.destroy();
    };
  }, [
    labels,
    datasets,
    options,
    stacked,
    maintainAspectRatio,
    roundedBars,
    barSpacing,
    cleanGrid,
    pointRadius,
    pointHoverRadius,
    lineTension,
  ]);

  const getFormattedValue = (value, label) => {
    if (currency && labelsToDisplayCurrency.includes(label)) {
      return formatCurrency(value, currency);
    }

    return value;
  }

  return (
    <div style={{ position: 'relative', width, height }}>
      <canvas ref={chartRef} />
      <ChartTooltip
        visible={tooltip.visible}
        x={tooltip.x}
        y={tooltip.y}
        content={
        <>
          {tooltip.allDataPoints.length > 1 ? (
            <FlexContainer flexFlow="column" gap="8px" style={{ minWidth: 150 }}>
              <span>{tooltip.title}</span>
              {tooltip.allDataPoints.map((dp, index) => (
                <FlexContainer key={index} alignItems="center" justifyContent="space-between" style={{ whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        background: dp.color,
                        borderRadius: 4,
                        marginRight: 8,
                      }}
                    />
                    <span style={{ color: 'var(--grey-64)' }}>
                      {dp.label}
                    </span>
                  </div>
                  <span style={{ color: 'var(--primary-color)', marginLeft: 12, fontWeight: 'bold' }}>
                    {getFormattedValue(dp.value, dp.label)}
                  </span>
                </FlexContainer>
              ))}
            </FlexContainer>
          ) : (
            <FlexContainer alignItems="center" style={{ minWidth: 90 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  background: tooltip.color,
                  borderRadius: 4,
                  marginRight: 12,
                }}
              />
              <span style={{ color: 'var(--grey-64)', marginRight: 12 }}>
                {tooltip.label}
              </span>
              <span style={{ color: 'var(--primary-color)' }}>
                {getFormattedValue(tooltip.value, tooltip.label)}
              </span>
            </FlexContainer>
          )}
        </>
      }
      />
    </div>
  );
};

BarsAndLineChart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.number).isRequired,
      backgroundColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      borderColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      borderWidth: PropTypes.number,
      yAxisID: PropTypes.string,
    })
  ).isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.object,
  stacked: PropTypes.bool,
  maintainAspectRatio: PropTypes.bool,
  roundedBars: PropTypes.bool,
  barSpacing: PropTypes.number,
  cleanGrid: PropTypes.bool,
  pointRadius: PropTypes.number,
  pointHoverRadius: PropTypes.number,
  lineTension: PropTypes.number,
};

BarsAndLineChart.defaultProps = {
  width: '100%',
  height: 300,
  options: {},
  stacked: false,
  maintainAspectRatio: false,
  roundedBars: true,
  barSpacing: 2,
  cleanGrid: true,
  pointRadius: 4,
  pointHoverRadius: 6,
  lineTension: 0,
};

export default BarsAndLineChart;
